/**
 * The 99 participating countries, plus Malaysia as host — slide 1's
 * "99 Countries, One Experience".
 *
 * Here rather than in the client because both sides need it: Admin picks a
 * country when entering the Best Traditional Attire winners, and the server
 * refuses a code that is not on this list rather than storing whatever was
 * typed. Free text put "Malasyia" one keystroke away from the stage screen.
 *
 * The codes are ISO 3166-1 alpha-2, which is what flagcdn serves images by.
 *
 * Israel is absent, as it is from every country list in this app, at the
 * Committee's instruction.
 */

export interface Country {
  /** ISO 3166-1 alpha-2, lower case — also the flag image key. */
  code: string
  name: string
}

/** Sorted by name, so a select reads alphabetically without re-sorting. */
export const COUNTRIES: readonly Country[] = [
  { code: 'af', name: 'Afghanistan' },
  { code: 'dz', name: 'Algeria' },
  { code: 'ao', name: 'Angola' },
  { code: 'ar', name: 'Argentina' },
  { code: 'au', name: 'Australia' },
  { code: 'bh', name: 'Bahrain' },
  { code: 'bd', name: 'Bangladesh' },
  { code: 'be', name: 'Belgium' },
  { code: 'bt', name: 'Bhutan' },
  { code: 'br', name: 'Brazil' },
  { code: 'bn', name: 'Brunei' },
  { code: 'kh', name: 'Cambodia' },
  { code: 'cm', name: 'Cameroon' },
  { code: 'ca', name: 'Canada' },
  { code: 'cl', name: 'Chile' },
  { code: 'cn', name: 'China' },
  { code: 'co', name: 'Colombia' },
  { code: 'ci', name: 'Côte d’Ivoire' },
  { code: 'cu', name: 'Cuba' },
  { code: 'dk', name: 'Denmark' },
  { code: 'ec', name: 'Ecuador' },
  { code: 'eg', name: 'Egypt' },
  { code: 'et', name: 'Ethiopia' },
  { code: 'fj', name: 'Fiji' },
  { code: 'fi', name: 'Finland' },
  { code: 'fr', name: 'France' },
  { code: 'de', name: 'Germany' },
  { code: 'gh', name: 'Ghana' },
  { code: 'gr', name: 'Greece' },
  { code: 'in', name: 'India' },
  { code: 'id', name: 'Indonesia' },
  { code: 'ir', name: 'Iran' },
  { code: 'iq', name: 'Iraq' },
  { code: 'ie', name: 'Ireland' },
  { code: 'it', name: 'Italy' },
  { code: 'jp', name: 'Japan' },
  { code: 'jo', name: 'Jordan' },
  { code: 'kz', name: 'Kazakhstan' },
  { code: 'ke', name: 'Kenya' },
  { code: 'kw', name: 'Kuwait' },
  { code: 'kg', name: 'Kyrgyzstan' },
  { code: 'la', name: 'Laos' },
  { code: 'lb', name: 'Lebanon' },
  { code: 'ly', name: 'Libya' },
  { code: 'mw', name: 'Malawi' },
  { code: 'my', name: 'Malaysia' },
  { code: 'mv', name: 'Maldives' },
  { code: 'ml', name: 'Mali' },
  { code: 'mu', name: 'Mauritius' },
  { code: 'mx', name: 'Mexico' },
  { code: 'mn', name: 'Mongolia' },
  { code: 'ma', name: 'Morocco' },
  { code: 'mz', name: 'Mozambique' },
  { code: 'mm', name: 'Myanmar' },
  { code: 'np', name: 'Nepal' },
  { code: 'nl', name: 'Netherlands' },
  { code: 'nz', name: 'New Zealand' },
  { code: 'ng', name: 'Nigeria' },
  { code: 'no', name: 'Norway' },
  { code: 'om', name: 'Oman' },
  { code: 'pk', name: 'Pakistan' },
  { code: 'ps', name: 'Palestine' },
  { code: 'pg', name: 'Papua New Guinea' },
  { code: 'pe', name: 'Peru' },
  { code: 'ph', name: 'Philippines' },
  { code: 'pl', name: 'Poland' },
  { code: 'pt', name: 'Portugal' },
  { code: 'qa', name: 'Qatar' },
  { code: 'ru', name: 'Russia' },
  { code: 'rw', name: 'Rwanda' },
  { code: 'sa', name: 'Saudi Arabia' },
  { code: 'sn', name: 'Senegal' },
  { code: 'sg', name: 'Singapore' },
  { code: 'so', name: 'Somalia' },
  { code: 'za', name: 'South Africa' },
  { code: 'kr', name: 'South Korea' },
  { code: 'es', name: 'Spain' },
  { code: 'lk', name: 'Sri Lanka' },
  { code: 'sd', name: 'Sudan' },
  { code: 'se', name: 'Sweden' },
  { code: 'ch', name: 'Switzerland' },
  { code: 'sy', name: 'Syria' },
  { code: 'tj', name: 'Tajikistan' },
  { code: 'tz', name: 'Tanzania' },
  { code: 'th', name: 'Thailand' },
  { code: 'tl', name: 'Timor-Leste' },
  { code: 'tn', name: 'Tunisia' },
  { code: 'tr', name: 'Türkiye' },
  { code: 'tm', name: 'Turkmenistan' },
  { code: 'ug', name: 'Uganda' },
  { code: 'ua', name: 'Ukraine' },
  { code: 'ae', name: 'United Arab Emirates' },
  { code: 'gb', name: 'United Kingdom' },
  { code: 'us', name: 'United States' },
  { code: 'uz', name: 'Uzbekistan' },
  { code: 've', name: 'Venezuela' },
  { code: 'vn', name: 'Vietnam' },
  { code: 'ye', name: 'Yemen' },
  { code: 'zm', name: 'Zambia' },
  { code: 'zw', name: 'Zimbabwe' },
] as const

const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]))

export function countryName(code: string | null | undefined): string | null {
  return code == null ? null : (BY_CODE.get(code.trim().toLowerCase())?.name ?? null)
}

export function isCountryCode(code: string): boolean {
  return BY_CODE.has(code.trim().toLowerCase())
}
